// Checkpoint readout math for the race HUD.
//
// Pure functions only: given the current run's cumulative split (ticks), the
// leaderboard entries and the player's PB, derive the rank at this checkpoint,
// the PB delta, the gap to the next faster run and the gap to the WR. The DOM
// renderer lives in main.ts; this file is what unit tests exercise.

import { ticksToMs } from "../tickrate";
import type { LeaderboardEntry } from "../net/leaderboard";

export interface CheckpointReadout {
  /** 1-based checkpoint number. */
  cp: number;
  /** 1-based position at this checkpoint (1 when there is nothing to compare). */
  rank: number;
  /** current - PB (negative = faster). */
  pbDeltaMs: number | null;
  /** current - next faster run (always >= 0). */
  nextGapMs: number | null;
  /** current - WR (negative = ahead of the WR at this checkpoint). */
  wrGapMs: number | null;
  nextName: string | null;
  wrName: string | null;
  /** How many leaderboard runs have a split at this checkpoint. */
  entriesAtCp: number;
}

/** Cumulative tick split at `index`, or null when the row lacks valid data. */
function splitAt(entry: LeaderboardEntry | null | undefined, index: number): number | null {
  const splits = entry?.splits;
  if (!Array.isArray(splits) || splits.length <= index) return null;
  const ticks = Number(splits[index]);
  return Number.isFinite(ticks) && ticks > 0 ? ticks : null;
}

/**
 * `cpIndex` is zero-based (the just-passed checkpoint). `entries` is ordered by
 * final time (the leaderboard order), so `entries[0]` is the WR run.
 */
export function computeCheckpointReadout(
  currentTicks: number,
  cpIndex: number,
  entries: LeaderboardEntry[],
  me: LeaderboardEntry | null,
): CheckpointReadout {
  const currentMs = ticksToMs(currentTicks);
  const rows: { t: number; e: LeaderboardEntry }[] = [];
  for (const entry of entries) {
    const t = splitAt(entry, cpIndex);
    if (t !== null) rows.push({ t, e: entry });
  }
  rows.sort((a, b) => a.t - b.t);

  const faster = rows.filter((r) => r.t < currentTicks);
  const rank = faster.length + 1;
  const next = faster.length ? faster[faster.length - 1] : null;

  // The WR is the fastest final time. Old runs may predate split recording; in
  // that case fall back to the fastest checkpoint split we actually have.
  const wrTicks = splitAt(entries[0], cpIndex) ?? rows[0]?.t ?? null;
  const pbTicks = splitAt(me, cpIndex);

  return {
    cp: cpIndex + 1,
    rank,
    pbDeltaMs: pbTicks !== null ? Math.round(currentMs - ticksToMs(pbTicks)) : null,
    nextGapMs: next ? Math.round(currentMs - ticksToMs(next.t)) : null,
    wrGapMs: wrTicks !== null ? Math.round(currentMs - ticksToMs(wrTicks)) : null,
    nextName: next?.e.nickname ?? null,
    wrName: entries[0]?.nickname ?? null,
    entriesAtCp: rows.length,
  };
}
